import { Toaster } from "@/components/ui/sonner";
import { VlyToolbar } from "../vly-toolbar-readonly.tsx";
import { InstrumentationProvider } from "@/instrumentation.tsx";
import { ConvexAuthProvider } from "@convex-dev/auth/react";
import { ConvexReactClient } from "convex/react";
import { StrictMode, useEffect } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { BrowserRouter, useLocation } from "react-router";
import { HelmetProvider } from "react-helmet-async";
import "./index.css";
import App from "./App.tsx";
import "./types/global.d.ts";
import Admin from "@/pages/Admin.tsx";
import AdminCMS from "@/pages/AdminCMS.tsx";
import AdminProducts from "@/pages/AdminProducts.tsx";
import AdminBlogs from "@/pages/AdminBlogs.tsx";
import AdminFiles from "@/pages/AdminFiles.tsx";
import MediaViewer from "@/pages/MediaViewer.tsx";
import { Routes, Route } from "react-router";

const convexUrl = import.meta.env.VITE_CONVEX_URL;

if (!convexUrl) {
  throw new Error(
    "Missing VITE_CONVEX_URL environment variable.\\n\\n" +
    "For local development: Run 'npx convex dev' to start the development server.\\n" +
    "For Cloudflare Pages: Set VITE_CONVEX_URL in your Cloudflare dashboard under Settings > Environment Variables.\\n" +
    "The URL should look like: https://your-deployment.convex.cloud"
  );
}

const convex = new ConvexReactClient(convexUrl);

// Make the Convex client globally available for hooks
if (typeof window !== 'undefined') {
  (window as any).__convexClient = convex;
}

function RouteSyncer() {
  const location = useLocation();
  useEffect(() => {
    window.parent.postMessage(
      { type: "iframe-route-change", path: location.pathname },
      "*",
    );
  }, [location.pathname]);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.data?.type === "navigate") {
        if (event.data.direction === "back") window.history.back();
        if (event.data.direction === "forward") window.history.forward();
      }
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return null;
}

const rootElement = document.getElementById("root")!;

const app = (
  <StrictMode>
    <VlyToolbar />
    <InstrumentationProvider>
      <ConvexAuthProvider client={convex}>
        <HelmetProvider>
          <BrowserRouter>
            <RouteSyncer />
            <Routes>
              <Route path="/" element={<App />} />
              <Route path="/media/:slug" element={<MediaViewer />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/cms" element={<AdminCMS />} />
              <Route path="/admin/products" element={<AdminProducts />} />
              <Route path="/admin/cms/blogs" element={<AdminBlogs />} />
              <Route path="/admin/files" element={<AdminFiles />} />
              <Route path="*" element={<App />} />
            </Routes>
          </BrowserRouter>
        </HelmetProvider>
        <Toaster />
      </ConvexAuthProvider>
    </InstrumentationProvider>
  </StrictMode>
);

// Use hydration if SSR content exists, otherwise render normally
if (rootElement.innerHTML.trim()) {
  hydrateRoot(rootElement, app);
} else {
  createRoot(rootElement).render(app);
}